/**
 * «Повідомити, коли буде XL» on the product page (bot/SPEC.md §2, §7; design/SPEC.md: Розмір відсутній).
 *
 * The link opens the Telegram bot with r_<variant>_<nonce> (drops: d_<product>_<nonce>, «Нагадати» /
 * «Отримати на добу раніше»). The nonce is this browser's handle on the
 * subscription: the page asks the bot service (theme setting kh_bot_api_url) for its status every 2 s
 * while the person presses Start, then shows «Повідомимо про XL» with «Скасувати». The card «Ще один крок —
 * у Telegram» (board V1-TgCard) takes the button's place: on desktop with a QR (the page stays), on a phone
 * compact without QR while Telegram opens straight away. The nonce is kept in localStorage per start parameter,
 * so the state survives a reload. Without the service URL the button is a plain bot link.
 */

const POLL_EVERY_MS = 2000;
const POLL_FOR_MS = 5 * 60 * 1000;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/**
 * Restock keys stay `kh-notify:<variant>` (nonces saved before drops existed); drops — `kh-notify:d_<product>`.
 * @param {string} start - r_<variant> or d_<product>
 */
const storageKey = (start) => `kh-notify:${start.startsWith('r_') ? start.slice(2) : start}`;

/** @param {string} start */
function readNonce(start) {
  try {
    return localStorage.getItem(storageKey(start)) || '';
  } catch {
    return '';
  }
}

/** @param {string} start @param {string | null} nonce */
function writeNonce(start, nonce) {
  try {
    if (nonce) localStorage.setItem(storageKey(start), nonce);
    else localStorage.removeItem(storageKey(start));
  } catch {
    /* private mode: the card still works for this page view */
  }
}

function newNonce() {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join('');
}

const isDesktop = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

class KhNotify extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #deadline = 0;

  /** Start parameter without the nonce: r_<variant> (restock) or d_<product> (drop). */
  get #start() {
    return this.dataset.start || `r_${this.dataset.variant || ''}`;
  }

  get #api() {
    return (this.dataset.api || '').replace(/\/$/, '');
  }

  connectedCallback() {
    this.querySelector('[data-kh-notify-link]')?.addEventListener('click', this.#onClick);
    this.querySelectorAll('[data-kh-notify-cancel]').forEach((button) => button.addEventListener('click', this.#onCancel));
    document.addEventListener('visibilitychange', this.#onVisible);
    const nonce = readNonce(this.#start);
    if (nonce && this.#api) this.#check(nonce, false);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisible);
  }

  /** @param {string} nonce */
  #botUrl(nonce) {
    return `https://t.me/${this.dataset.bot}?start=${this.#start}_${nonce}`;
  }

  /** @param {'idle' | 'qr' | 'done'} state */
  #show(state) {
    const parts = {
      idle: this.querySelector('[data-kh-notify-idle]'),
      qr: this.querySelector('[data-kh-notify-qr]'),
      done: this.querySelector('[data-kh-notify-done]'),
    };
    for (const [name, element] of Object.entries(parts)) {
      if (element) /** @type {HTMLElement} */ (element).hidden = name !== state;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#api) return; // no service: a plain link to the bot
    const nonce = readNonce(this.#start) || newNonce();
    writeNonce(this.#start, nonce);
    const url = this.#botUrl(nonce);
    const link = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    link.href = url;
    const desktop = isDesktop();
    if (desktop) event.preventDefault(); // phone: Telegram opens now, the card waits for the return
    this.#showCard(url, desktop);
    this.#deadline = Date.now() + POLL_FOR_MS;
    this.#schedule(nonce);
  };

  /**
   * @param {string} url
   * @param {boolean} withQr - desktop; on a phone the card is compact (no QR)
   */
  async #showCard(url, withQr) {
    const open = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-kh-notify-open]'));
    if (open) open.href = url;
    this.querySelector('[data-kh-notify-qr]')?.classList.toggle('is-compact', !withQr);
    this.#show('qr');
    const box = this.querySelector('[data-kh-notify-qr-img]');
    if (!withQr || !box || !this.dataset.qrSrc) return;
    try {
      const { QRCode } = await import(this.dataset.qrSrc);
      box.replaceChildren();
      // Rendered at 2× and shown at 125 px: crisp modules, scannable from a laptop screen at 30–40 cm.
      new QRCode(box, { text: url, width: 250, height: 250, alt: '' });
    } catch {
      box.replaceChildren(); // the «Відкрити Telegram» button still works
    }
  }

  /** @param {string} nonce */
  #schedule(nonce) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#check(nonce, true), POLL_EVERY_MS);
  }

  /** Back from Telegram on a phone: ask right away instead of waiting for the next tick. */
  #onVisible = () => {
    if (document.visibilityState !== 'visible' || !this.#api) return;
    const nonce = readNonce(this.#start);
    if (nonce && this.#deadline > Date.now()) this.#check(nonce, true);
  };

  /**
   * @param {string} nonce
   * @param {boolean} polling - keep asking until subscribed or the deadline
   */
  async #check(nonce, polling) {
    let status = 'none';
    try {
      const response = await fetch(`${this.#api}/api/sub?n=${encodeURIComponent(nonce)}`, { cache: 'no-store' });
      if (response.ok) status = (await response.json()).status || 'none';
    } catch {
      /* offline or service down: try again on the next tick */
    }
    if (nonce !== readNonce(this.#start)) return; // cancelled meanwhile

    if (status === 'subscribed') {
      window.clearTimeout(this.#timer);
      this.#show('done');
      return;
    }
    if (status === 'notified' || status === 'blocked') {
      // Already messaged about this size / drop, or the bot is blocked: offer the button again with a fresh nonce.
      writeNonce(this.#start, null);
      window.clearTimeout(this.#timer);
      this.#show('idle');
      return;
    }
    if (polling && Date.now() < this.#deadline) {
      this.#schedule(nonce);
    } else if (polling) {
      this.#show('idle'); // gave up waiting; the nonce stays for the next try
    }
  }

  #onCancel = async () => {
    window.clearTimeout(this.#timer);
    const nonce = readNonce(this.#start);
    writeNonce(this.#start, null);
    this.#show('idle');
    if (!nonce || !this.#api) return;
    try {
      await fetch(`${this.#api}/api/sub?n=${encodeURIComponent(nonce)}&action=cancel`, { method: 'POST' });
    } catch {
      /* best effort: the bot's /stop also removes it */
    }
  };
}

if (!customElements.get('kh-notify')) customElements.define('kh-notify', KhNotify);
